import type { ScenariaPlan } from "src/model/model";

const GENERIC_360 = "Environnement de travail";

export const MAX_SEED_LEN = 400;

export const resolveTrunk = (plan: ScenariaPlan): string => {
  const validImages = (plan.attachedImages || []).filter(
    (img) => img && !img.failed && img.description && img.description.trim().length > 0,
  );
  if (validImages.length > 0) {
    return validImages
      .map((img) => img.description.trim())
      .join(". ")
      .slice(0, MAX_SEED_LEN);
  }
  const env = typeof plan.visualEnvironment === "string" ? plan.visualEnvironment.trim() : "";
  if (env.length > 0) return env.slice(0, MAX_SEED_LEN);
  const audience = typeof plan.targetAudience === "string" ? plan.targetAudience.trim() : "";
  return (audience ? `Environnement de travail correspondant à : ${audience}` : GENERIC_360).slice(
    0,
    MAX_SEED_LEN,
  );
};

export const composeB2Prompt = (trunk: string, variation: string): string => {
  const separator = ", ";
  const budget = MAX_SEED_LEN - trunk.length - separator.length;
  if (budget <= 0) return trunk;
  if (variation.length <= budget) return `${trunk}${separator}${variation}`;
  const window = variation.slice(0, budget - 1);
  const lastSpace = window.lastIndexOf(" ");
  const cut = lastSpace > budget * 0.6 ? lastSpace : window.length;
  return `${trunk}${separator}${variation.slice(0, cut).trimEnd()}…`;
};

export const renderPlanToMarkdown = (plan: ScenariaPlan): string => {
  const header =
    "# Scénario\n" +
    `Public cible: ${plan.targetAudience}\n` +
    "Objectifs:\n" +
    plan.learningObjectives.map((o) => `- ${o}`).join("\n") +
    (plan.constraints && plan.constraints.length
      ? `\nContraintes:\n${plan.constraints.map((c) => `- ${c}`).join("\n")}`
      : "") +
    (plan.stylisticPreferences && plan.stylisticPreferences.tone
      ? `\nTon: ${plan.stylisticPreferences.tone}`
      : "") +
    (plan.stylisticPreferences && plan.stylisticPreferences.length
      ? `\nLongueur souhaitée: ${plan.stylisticPreferences.length}`
      : "") +
    "\n\n";

  const scenes = plan.sceneOutline
    .map((scene, i) => {
      const mediaLabel = scene.mediaType === "video360" ? "Vidéo 360" : "Image 360";
      const refIds = scene.attachedImageRefIds || [];
      const imageDesc =
        refIds.length && plan.attachedImages
          ? plan.attachedImages.find((img) => refIds.includes(img.mediaId))?.description
          : null;
      let description360: string;
      if (
        typeof scene.sceneVisualVariation === "string" &&
        scene.sceneVisualVariation.trim().length > 0
      ) {
        description360 = composeB2Prompt(resolveTrunk(plan), scene.sceneVisualVariation.trim());
      } else if (scene.blockadePromptSeed) {
        description360 = scene.blockadePromptSeed;
      } else {
        description360 = imageDesc || plan.visualEnvironment || GENERIC_360;
      }
      const interactionLines =
        scene.interactions && scene.interactions.length
          ? scene.interactions
              .map((inter) =>
                inter && inter.intent
                  ? `- **${inter.type}**: ${inter.intent}`
                  : `- **${inter.type}**`,
              )
              .join("\n") + "\n"
          : "";
      return (
        `### Scène n° ${i + 1}: ${scene.title}\n` +
        `${scene.summary}\n` +
        `Média: ${mediaLabel}\n` +
        `360 - ${description360}\n` +
        interactionLines
      );
    })
    .join("\n");

  return header + scenes;
};
